import { captureMsgraphReturn, isSafeConsentReturn } from './features/me/msgraphReturn'
import { captureAgentConsoleConsentReturn } from './features/msgraph/agentAccessLinks'
import React, { Suspense, lazy } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { BrandingProvider } from './platform/context/BrandingContext'
import { ThemeProvider } from './platform/context/ThemeContext'
import ReactDOM from 'react-dom/client'
import { MsalProvider } from '@azure/msal-react'
import { PublicClientApplication, EventType } from '@azure/msal-browser'
import { buildMsalConfig, resolveAuthIdentity } from './platform/authConfig'
import { isMsalResponseFrame } from './platform/msalResponseFrame'
import App from './App'
import '@fontsource-variable/geist'
import '@fontsource-variable/geist-mono'
import '@coreui/coreui/dist/css/coreui.min.css'
// Radix color scales — the colour source for the whole app. Light files bind
// to :root, *-dark.css to the .dark class kept in sync with data-coreui-theme
// by ThemeContext. Components never use these directly; tokens.css maps
// semantic --app-* roles onto steps.
import '@radix-ui/colors/slate.css'
import '@radix-ui/colors/slate-dark.css'
import '@radix-ui/colors/cyan.css'
import '@radix-ui/colors/cyan-dark.css'
import '@radix-ui/colors/red.css'
import '@radix-ui/colors/red-dark.css'
import '@radix-ui/colors/amber.css'
import '@radix-ui/colors/amber-dark.css'
import '@radix-ui/colors/green.css'
import '@radix-ui/colors/green-dark.css'
import '@radix-ui/colors/blue.css'
import '@radix-ui/colors/blue-dark.css'
import '@radix-ui/colors/violet.css'
import '@radix-ui/colors/violet-dark.css'
// pink + teal exist for the categorical chart palette only (config/chartColors.js)
import '@radix-ui/colors/pink.css'
import '@radix-ui/colors/pink-dark.css'
import '@radix-ui/colors/teal.css'
import '@radix-ui/colors/teal-dark.css'
import './index.css'
import './styles/tokens.css'
import './styles/tailwind.css'
import './styles/ops-skin.css'
import './styles/view-preferences.css'

// MSAL 5 renews a token in a hidden iframe that loads this console with the
// sign-in answer in its address, and the console tab waits for that frame to
// post the answer back (MSAL's redirect bridge). This frame does only that. It
// used to start the whole console instead: its MSAL refused to run there
// (block_iframe_reload), its API calls went out without a token, the answer
// never reached the tab, and the request that needed the renewal failed with a
// 401 ("send failed" in Investigate).
const msalResponseFrame = typeof window !== 'undefined' && isMsalResponseFrame(window)

// Normalize the broker return before MSAL reads fragments or starts login.
if (typeof window !== 'undefined' && !msalResponseFrame) captureMsgraphReturn(window)
// Microsoft's answer to an area's console consent ("Approve area", step 2)
// arrives on the console origin; move it to Integrations → Microsoft 365 first.
if (typeof window !== 'undefined' && !msalResponseFrame) captureAgentConsoleConsentReturn(window)

// Impersonation handoff. The admin worker hands off an opaque
// `imp_*` token via the URL fragment (`/#/impersonation-handoff?token=imp_...`).
// Fragments never reach the server, the CF Pages logs, or browser
// history — they only live in window.location.hash on this tab. We
// capture it synchronously before MSAL initializes so that:
//   (a) the apiClient request interceptor finds the token on its
//       first call (no race with MSAL silent-acquire), and
//   (b) AppRouter's `isAuthenticated` check below treats the
//       impersonation session as authenticated, skipping the
//       login redirect that would otherwise send the operator into
//       the customer's tenant.
// Token never lives in localStorage. `replaceState` strips the
// fragment from the URL bar before the SPA renders.
;(function captureImpersonationHandoff() {
  const hash = window.location.hash
  if (!hash.startsWith('#/impersonation-handoff')) return
  const queryStart = hash.indexOf('?')
  if (queryStart === -1) return
  const params = new URLSearchParams(hash.slice(queryStart + 1))
  const token = params.get('token')
  if (token && token.startsWith('imp_')) {
    sessionStorage.setItem('impersonationToken', token)
  }
  window.history.replaceState(null, '', window.location.pathname + window.location.search)
})()

// Assigned during bootstrap, once the Entra identifiers are resolved. MSAL
// needs a client_id at construction, and for a newly provisioned customer that
// value does not exist until their admin has granted consent — so construction
// waits for the runtime lookup rather than baking an empty string.
export let msalInstance = null

// PR AV — register the Web Push service worker on app boot. The SW
// handles `push` + `notificationclick` events; the NotificationToggle
// component owns subscribe / unsubscribe via PushManager. Registration
// is best-effort: a browser without serviceWorker support, an old SW
// blocked by HTTP, or a registration that throws all degrade silently
// — the toggle UI surfaces the unsupported state to the operator.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch((err) => {
      console.warn('Service worker registration failed:', err)
    })
  })
}

// Mount the SPA — but ONLY when a real DOM root exists. Importing this module
// solely for its `msalInstance` export (the arch-graph frontend-half emit loads
// the feature graph → feature clients → apiClient → this module, design §5.6)
// must NOT mount the app: there is no #root in that Node/SSR context.
function mountApp() {
  const rootEl = typeof document !== 'undefined' ? document.getElementById('root') : null
  if (!rootEl) return
  ReactDOM.createRoot(rootEl).render(
    <MsalProvider instance={msalInstance}>
      <App />
    </MsalProvider>,
  )
}

// Shown instead of the SPA when the customer's admin has not yet granted
// consent. Without this the only symptom is a Microsoft error page.
function renderNotConnected() {
  const rootEl = typeof document !== 'undefined' ? document.getElementById('root') : null
  if (!rootEl) return
  rootEl.innerHTML =
    '<div style="font-family:system-ui;max-width:34rem;margin:4rem auto;padding:0 1rem">' +
    '<h1 style="font-size:1.25rem">Microsoft sign-in is not connected yet</h1>' +
    '<p>Your administrator needs to complete the one-time Microsoft Entra ID setup ' +
    'from the link in your welcome email. Once that is done, reload this page.</p></div>'
}

// Resolve the Entra identifiers, then start MSAL. Construction cannot happen
// at module load: a customer's tenant is only known after their admin grants
// consent, which is after this bundle was built and deployed.
async function bootstrap() {
  if (window.location.pathname.replace(/\/+$/, '') === '/docs/shared') {
    const rootEl = document.getElementById('root')
    if (rootEl) {
      const SharedDoc = lazy(() => import('./features/docs/SharedDoc'))
      ReactDOM.createRoot(rootEl).render(<ThemeProvider><BrandingProvider><BrowserRouter><Suspense fallback={null}><SharedDoc /></Suspense></BrowserRouter></BrandingProvider></ThemeProvider>)
    }
    return
  }
  const identity = await resolveAuthIdentity()

  if (!identity.clientId || !identity.tenantId) {
    // Constructing MSAL anyway sends the user to Microsoft with an empty
    // client_id and fails there (AADSTS900144) with nothing explaining why.
    // An impersonating operator is the exception: they authenticate with the
    // handoff token, never MSAL, so they must still reach the app.
    console.error('[auth] Microsoft Entra ID is not connected for this environment yet.')
    if (!sessionStorage.getItem('impersonationToken')) {
      renderNotConnected()
      return
    }
  }

  msalInstance = new PublicClientApplication(buildMsalConfig(identity))

  try {
    await msalInstance.initialize()
    const response = await msalInstance.handleRedirectPromise()
    if (response) {
      console.log('Login successful:', response.account.username)
      // A consent deep link stashed before the redirect (App.jsx
      // stashConsentReturn) is put back before the app mounts.
      try {
        const returnTo = sessionStorage.getItem('oauthConsentReturn')
        if (isSafeConsentReturn(returnTo)) {
          sessionStorage.removeItem('oauthConsentReturn')
          window.history.replaceState(null, '', returnTo)
        }
      } catch { /* private mode */ }
    }
    const accounts = msalInstance.getAllAccounts()
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0])
    }
    msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
        msalInstance.setActiveAccount(event.payload.account)
      }
    })
  } catch (error) {
    console.error('Redirect handling failed:', error)
  }

  // Render regardless of redirect outcome, as before.
  mountApp()
}

// Floating on purpose — nothing awaits module evaluation — but a rejection
// here would leave a blank page with only an unhandled-rejection in the log.
if (msalResponseFrame) {
  import('@azure/msal-browser/redirect-bridge')
    .then(({ broadcastResponseToMainFrame }) => broadcastResponseToMainFrame())
    .catch((error) => console.error('[auth] The sign-in answer could not be passed back to the console tab:', error))
} else {
  bootstrap().catch((error) => {
    console.error('Auth bootstrap failed:', error)
    renderNotConnected()
  })
}
